import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

// Skin Therapy KC. This module is intentionally separate from the theme's cart code.
const VARIANT_ID = 68174109900999;
const SELECTOR = 'shipping-protection-component';
const ownEvents = new WeakSet();
const pendingThemeUpdates = new Set();
const lockedElements = new Map();
let running = false;
let requested = false;
let intent;
let latestCart;
let errorMessage = '';
let mustVerify = false;
let timer;
let quietUntil = 0;
let focusId;
let refreshRequired = false;

const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isProtection = (item) => Number(item.variant_id) === VARIANT_ID;
const protectionLines = (cart) => cart.items.filter(isProtection);
const hasShippedProducts = (cart) => cart.items.some((item) => !isProtection(item) && item.requires_shipping);
const components = () => [...document.querySelectorAll('cart-items-component[data-section-id]')];
const root = () => window.Shopify?.routes?.root || '/';

function validCart(cart) {
  const lines = protectionLines(cart);
  return lines.length === 0 || (hasShippedProducts(cart) && lines.length === 1 && lines[0].quantity === 1);
}

// Only protection line keys are sent in updates; other quantities, notes and discounts are omitted.
function protectionPlan(cart, desired) {
  const lines = protectionLines(cart);
  const wanted = hasShippedProducts(cart) && (desired === undefined ? lines.length > 0 : desired);
  if (wanted && !lines.length) {
    return { endpoint: 'cart/add.js', body: { items: [{ id: VARIANT_ID, quantity: 1 }] },
      action: 'add', lines: [{ merchandiseId: String(VARIANT_ID), quantity: 1 }] };
  }
  const changes = lines.map((item, index) => ({ id: item.key, quantity: wanted && index === 0 ? 1 : 0 }))
    .filter((change, index) => change.quantity !== lines[index].quantity);
  if (!changes.length) return null;
  return { endpoint: 'cart/update.js', body: { updates: Object.fromEntries(changes.map((line) => [line.id, line.quantity])) },
    action: changes.every((line) => line.quantity === 0) ? 'remove' : 'update', lines: changes };
}

async function request(url, body) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(url, {
      method: body === undefined ? 'GET' : 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal: controller.signal,
    });
    const data = await response.json();
    if (!response.ok || data.errors || data.status >= 400) {
      const reason = data.description || data.message || data.errors;
      throw new Error(typeof reason === 'string' ? reason : 'The cart could not be updated.');
    }
    return data;
  } finally {
    clearTimeout(timeout);
  }
}

async function readCart() {
  const cart = await request(`${root()}cart.js`);
  if (!Array.isArray(cart.items) || typeof cart.item_count !== 'number') throw new Error('The cart could not be verified.');
  return cart;
}

function sectionIds() {
  return [...new Set(components().map((element) => element.dataset.sectionId))].slice(0, 5);
}


async function getSections(ids) {
  if (!ids.length) return {};

  const paths = [
    window.location.pathname + window.location.search,
    root(),
    `${root()}cart`
  ];

  let lastError;

  for (let attempt = 0; attempt < 3; attempt++) {
    const path = paths[attempt];
    const url = new URL(path, window.location.origin);

    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    url.searchParams.set('sections', ids.join(','));

    try {
      const sections = await request(url.href);

      const missing = ids.filter(
        (id) =>
          typeof sections?.[id] !== 'string' ||
          !sections[id].trim()
      );

      if (missing.length === 0) {
        return sections;
      }

      console.warn(
        '[shipping-protection] Missing cart sections:',
        missing,
        'Attempt:',
        attempt + 1
      );

      lastError = new Error(
        'The cart display could not be refreshed.'
      );
    } catch (error) {
      lastError = error;
      console.warn(
        '[shipping-protection] Section refresh attempt failed:',
        attempt + 1,
        error
      );
    }

    if (attempt < 2) {
      await pause(300 * (attempt + 1));
    }
  }

  throw lastError || new Error(
    'The cart display could not be refreshed.'
  );
}

function eventResult(cart, sections, didError = false) {
  return {
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    userErrors: [], warnings: [],
    detail: { sections, items: cart.items, itemCount: cart.item_count, source: 'shipping-protection', didError },
  };
}

async function renderSource(source, sections) {
  if (!source?.dataset.sectionId || !sections[source.dataset.sectionId]) return;
  await morphSection(source.dataset.sectionId, sections[source.dataset.sectionId], {
    mode: source.hasAttribute('data-drawer') ? 'hydration' : 'full', injectStylesheet: true,
  });
  for (const selector of document.querySelectorAll('cart-quantity-selector-component')) selector.updateButtonStates?.();
  paint();
}

async function refreshDisplay(cart, previousCart) {
  const source = components()[0] || document;
  const sections = await getSections(sectionIds());
  await renderSource(source, sections);
  const lines = cart.items.length ? cart.items.map((item) => ({ id: item.key, quantity: item.quantity })) :
    (previousCart?.items || []).map((item) => ({ id: item.key, quantity: 0 }));
  if (lines.length) {
    const event = new CartLinesUpdateEvent({ action: cart.items.length ? 'update' : 'remove', context: 'cart',
      lines, promise: Promise.resolve(eventResult(cart, sections)) });
    ownEvents.add(event);
    source.dispatchEvent(event);
  }
  refreshRequired = false;
}

async function mutate(plan) {
  const source = components()[0] || document;
  const ids = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  // Prevent an unhandled rejection if no other theme listeners are mounted.
  deferred.promise.catch(() => {});
  const event = new CartLinesUpdateEvent({ action: plan.action, context: 'cart', lines: plan.lines, promise: deferred.promise });
  ownEvents.add(event);
  source.dispatchEvent(event);
  try {
    const response = await request(`${root()}${plan.endpoint}`, {
      ...plan.body, ...(ids.length ? { sections: ids, sections_url: window.location.pathname } : {}),
    });
    // add.js returns added lines, not the complete cart.
    const cart = plan.endpoint === 'cart/add.js' ? await readCart() : response;
    if (!Array.isArray(cart.items) || typeof cart.item_count !== 'number') throw new Error('The cart could not be verified.');
    const sections = ids.every((id) => typeof response.sections?.[id] === 'string' && response.sections[id].trim())
      ? response.sections || {} : await getSections(ids);
    latestCart = cart;
    await renderSource(source, sections);
    deferred.resolve(eventResult(cart, sections));
    return cart;
  } catch (error) {
    // A failed response can follow a successful server write. Read back; never blindly retry a POST.
    try {
      latestCart = await readCart();
      const sections = await getSections(ids);
      await renderSource(source, sections);
      const result = eventResult(latestCart, sections, true);
      result.userErrors = [{ code: 'INVALID', message: error.message }];
      deferred.resolve(result);
      mustVerify = !validCart(latestCart);
    } catch {
      mustVerify = true;
      refreshRequired = true;
      deferred.reject(error);
    }
    source.dispatchEvent(new CartErrorEvent({ error: error.message || 'Unable to update shipping protection.', code: 'SERVICE_UNAVAILABLE' }));
    throw error;
  }
}

function paint() {
  const busy = running || requested || pendingThemeUpdates.size > 0 || Date.now() < quietUntil;
  const lockCheckout = busy || mustVerify;
  const toLock = new Set(document.querySelectorAll(
    running ? 'cart-items-component .cart-form, cart-items-component .cart-actions, cart-items-component .cart__ctas' :
      lockCheckout ? 'cart-items-component .cart__ctas' : ':not(*)'
  ));
  for (const [element, previous] of lockedElements) {
    if (!toLock.has(element)) { element.inert = previous; lockedElements.delete(element); }
  }
  for (const element of toLock) {
    if (!lockedElements.has(element)) lockedElements.set(element, element.inert);
    element.inert = true;
  }
  document.documentElement.classList.toggle('stk-protection-checkout-pending', Boolean(lockCheckout));
  for (const component of document.querySelectorAll(SELECTOR)) {
    const input = component.querySelector('input[data-protection-toggle]');
    if (!input) continue;
    const selected = latestCart ? protectionLines(latestCart).length > 0 : input.defaultChecked;
    const eligible = latestCart ? hasShippedProducts(latestCart) : component.dataset.eligible === 'true';
    component.hidden = !selected && (!eligible || component.dataset.available !== 'true') && !errorMessage;
    input.checked = selected;
    input.disabled = Boolean(busy || (!selected && (!eligible || component.dataset.available !== 'true')));
    component.setAttribute('aria-busy', String(Boolean(busy)));
    const status = component.querySelector('[data-protection-status]');
    const statusText = busy ? 'Updating cart…' : '';
    if (status && status.textContent !== statusText) status.textContent = statusText;
    const error = component.querySelector('[data-protection-error]');
    if (error) error.hidden = !errorMessage;
    const errorText = component.querySelector('[data-protection-error-text]');
    if (errorText && errorText.textContent !== errorMessage) errorText.textContent = errorMessage;
    const retry = component.querySelector('[data-protection-retry]');
    if (retry) retry.disabled = Boolean(busy);
  }
}

async function waitForTheme() {
  const deadline = Date.now() + 20000;
  // The supplied theme debounces quantity changes by 300 ms. Let that request start first.
  while (pendingThemeUpdates.size || Date.now() < quietUntil) {
    if (Date.now() > deadline) throw new Error('Another cart update is still running. Please refresh the cart.');
    await pause(50);
  }
}

async function process() {
  if (running || !requested) return;
  running = true;
  paint();
  try {
    while (requested) {
      await waitForTheme();
      requested = false;
      const desired = intent;
      intent = undefined;
      const work = async () => {
        mustVerify = true;
        const previousCart = latestCart;
        let cart = await readCart();
        latestCart = cart;
        for (let pass = 0; pass < 3; pass++) {
          const plan = protectionPlan(cart, desired);
          if (!plan) {
            if (refreshRequired) await refreshDisplay(cart, previousCart);
            mustVerify = false;
            return;
          }
          cart = await mutate(plan);
          latestCart = cart;
        }
        if (protectionPlan(cart, desired)) {
          mustVerify = true;
          throw new Error('Shipping protection could not be confirmed. Please refresh the cart.');
        }
        if (refreshRequired) await refreshDisplay(cart, previousCart);
        mustVerify = false;
      };
      // Serialize this add-on across tabs in browsers with the Web Locks API.
      if (navigator.locks?.request) await navigator.locks.request('skin-therapy-kc-shipping-protection', work);
      else await work();
    }
  } catch (error) {
    console.warn('[shipping-protection]', error);
    requested = false;
    intent = undefined;
    if (!latestCart || !validCart(latestCart)) mustVerify = true;
    errorMessage = 'We could not finish updating shipping protection. Check the cart below, then try again or refresh.';
  } finally {
    running = false;
    paint();
    if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
    focusId = undefined;
    if (requested) schedule();
  }
}

function schedule(desired) {
  if (typeof desired === 'boolean') intent = desired;
  requested = true;
  clearTimeout(timer);
  paint();
  timer = setTimeout(process, 0);
}

class ShippingProtectionComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    paint();
  }
  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
  }
  onChange = (event) => {
    if (!event.target.matches('input[data-protection-toggle]')) return;
    const desired = event.target.checked;
    focusId = event.target.id;
    errorMessage = '';
    // Also covers a quantity event that occurred just before this module loaded.
    quietUntil = Math.max(quietUntil, Date.now() + 350);
    schedule(desired);
  };
  onClick = (event) => {
    if (event.target.closest('[data-protection-retry]')) { errorMessage = ''; refreshRequired = true; schedule(); }
    if (event.target.closest('[data-protection-refresh]')) window.location.reload();
  };
}

if (!customElements.get(SELECTOR)) customElements.define(SELECTOR, ShippingProtectionComponent);

function trackThemeUpdate(event) {
  if (ownEvents.has(event) || !event.promise?.then) return;
  if (running) refreshRequired = true;
  const pending = event.promise;
  pendingThemeUpdates.add(pending);
  paint();
  const finish = () => { pendingThemeUpdates.delete(pending); if (running) refreshRequired = true; schedule(); };
  pending.then(finish, finish);
}

document.addEventListener(StandardEvents.cartLinesUpdate, trackThemeUpdate);
document.addEventListener(StandardEvents.cartDiscountUpdate, trackThemeUpdate);
document.addEventListener(StandardEvents.cartNoteUpdate, trackThemeUpdate);
document.addEventListener(ThemeEvents.quantitySelectorUpdate, () => {
  quietUntil = Date.now() + 350;
  schedule();
});

// Cover controls inserted by a theme render while a request is pending.
new MutationObserver(() => {
  if (running || requested || pendingThemeUpdates.size || mustVerify) paint();
}).observe(document.body, { childList: true, subtree: true });

// Inert protects wallet shadow trees. This also blocks form submissions while state is unverified.
document.addEventListener('submit', (event) => {
  if ((running || requested || pendingThemeUpdates.size || mustVerify) && event.target.matches('.cart-form')) {
    event.preventDefault(); event.stopImmediatePropagation();
  }
}, true);

window.addEventListener('pageshow', (event) => { if (event.persisted) { refreshRequired = true; schedule(); } });
window.addEventListener('focus', () => { refreshRequired = true; schedule(); });
document.addEventListener('theme-drawer:open', () => { refreshRequired = true; schedule(); });
schedule();
